/**
 * What a Zuprize Partner shows of its contact details, on its Butikker page
 * and its profile.
 *
 * A business selling to consumers must show who it is and how to reach it
 * (Lovdata, Forbrukertilsynet), so the legal company name (Firmanavn, not the
 * display name), org.nr., address, customer email and website always show
 * (Guro, 9 October). Only the phone is the partner's choice, in its profile
 * settings (`publicData.partnerContactVisibility.phone`), off until switched
 * on. The phone lives in protected data, so it is copied into public data
 * (`publicData.contactPhone`) only while it is switched on.
 *
 * Plain CommonJS so the server (api/partners.js) uses the same rule.
 */

const CONTACT_FIELDS = ['name', 'orgNumber', 'address', 'email', 'website', 'phone'];

const DEFAULT_VISIBILITY = {
  name: true,
  orgNumber: true,
  address: true,
  email: true,
  website: true,
  phone: false,
};

const ALWAYS_SHOWN = ['name', 'orgNumber', 'address', 'email', 'website'];

// The details a partner can switch on or off: the phone.
const OPTIONAL_FIELDS = CONTACT_FIELDS.filter(field => !ALWAYS_SHOWN.includes(field));

const clean = value => (typeof value === 'string' ? value.trim() : '');

/**
 * Which fields show, from the partner's public data.
 *
 * @param {Object} [publicData]
 * @returns {Object} { name, orgNumber, address, email, website, phone } → boolean
 */
const contactVisibility = (publicData = {}) => {
  const chosen = publicData?.partnerContactVisibility || {};
  return CONTACT_FIELDS.reduce((visibility, field) => {
    const value =
      ALWAYS_SHOWN.includes(field) ||
      (typeof chosen[field] === 'boolean' ? chosen[field] : DEFAULT_VISIBILITY[field]);
    return { ...visibility, [field]: value };
  }, {});
};

/**
 * "Storgata 1, 0155 Oslo", or null.
 *
 * @param {Object} [publicData]
 * @returns {string|null}
 */
const addressOf = (publicData = {}) => {
  const street = clean(publicData.streetAddress);
  const place = [clean(publicData.postalCode), clean(publicData.city)].filter(Boolean).join(' ');
  return [street, place].filter(Boolean).join(', ') || null;
};

/**
 * The contact details a partner shows, in display order, each with its value.
 * Fields switched off, or empty, are left out.
 *
 * @param {Object} [publicData]
 * @returns {Array<{ field: string, value: string }>}
 */
const visibleContact = (publicData = {}) => {
  const visibility = contactVisibility(publicData);
  const values = {
    name: clean(publicData.companyName),
    orgNumber: clean(publicData.orgNumber),
    address: addressOf(publicData),
    email: clean(publicData.contactEmail),
    website: clean(publicData.website),
    phone: clean(publicData.contactPhone),
  };
  return CONTACT_FIELDS.filter(field => visibility[field] && values[field]).map(field => ({
    field,
    value: values[field],
  }));
};

module.exports = {
  CONTACT_FIELDS,
  DEFAULT_VISIBILITY,
  ALWAYS_SHOWN,
  OPTIONAL_FIELDS,
  contactVisibility,
  visibleContact,
};
